/**
 * SettingsAccountControls — Iter 175i.
 *
 * `/website-builder/settings/account-controls`. Two long-promised features
 * for the client:
 *   1. **Export my data** — anytime, JSON or ZIP. No scheduling required.
 *   2. **Schedule deletion** — anytime, 1–30 day grace period, configurable
 *      reason. Useful when the owner wants to move to another platform.
 *
 * Both endpoints live on /api/builder/sites/me/* and are open to the
 * owner only (`require_auth`). Master initiated deletions cannot be
 * cancelled from here — the user has to appeal via support.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Download, ShieldAlert, Loader2, CalendarX, FileArchive, FileJson, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  GlassCard, GradientIconTile, DashboardBackdrop,
} from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function SettingsAccountControls() {
  const [exportBusy, setExportBusy] = useState(null);
  const [delDays, setDelDays] = useState(7);
  const [delReason, setDelReason] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [scheduled, setScheduled] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/sites/me/deletion-status`, { headers: auth() });
      setScheduled(r.data?.scheduled ? r.data : null);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doExport = async (fmt) => {
    setExportBusy(fmt);
    try {
      const r = await axios.get(`${API}/builder/sites/me/export?format=${fmt}`, {
        headers: auth(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: fmt === 'zip' ? 'application/zip' : 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_site_export.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} export ready`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExportBusy(null); }
  };

  const submitDeletion = async () => {
    if (!(await themedConfirm(`Schedule your site for deletion in ${delDays} day(s)? You'll be able to cancel anytime in that window from this page or from the dashboard banner.`))) return;
    setDelBusy(true);
    try {
      await axios.post(`${API}/builder/sites/me/schedule-deletion`, { days: delDays, reason: delReason }, { headers: auth() });
      toast.success(`Scheduled. We'll fully delete your site in ${delDays} day(s).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to schedule'); }
    finally { setDelBusy(false); }
  };

  const cancelDeletion = async () => {
    if (!(await themedConfirm('Cancel the scheduled deletion of your site?'))) return;
    try {
      await axios.post(`${API}/builder/sites/me/cancel-deletion`, {}, { headers: auth() });
      toast.success('Cancelled. Your site is fully active again.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
  };

  return (
    <div className="relative" data-testid="settings-account-controls">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />
        <div>
          <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Account Controls</p>
          <h1 className="text-3xl font-bold text-white font-syne mt-1">Export your data &amp; site lifecycle</h1>
          <p className="text-sm text-white/65 mt-2 max-w-xl">
            You own your data. Export it any time — to back it up or move to a new platform. You can also schedule your site for deletion with a configurable grace period.
          </p>
        </div>

        {/* Export */}
        <GlassCard glow className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <GradientIconTile icon={Download} ramp="cyan" />
            <div>
              <h2 className="text-white font-bold font-syne">Export everything</h2>
              <p className="text-xs text-white/55 mt-0.5">JSON dump (small &amp; portable) or ZIP (JSON + media URL manifest + readme).</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => doExport('json')}
              disabled={exportBusy === 'json'}
              className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.16em] bg-white/10 hover:bg-white/15 text-white border border-white/15 disabled:opacity-40 flex items-center gap-2"
              data-testid="export-json-btn"
            >
              {exportBusy === 'json' ? <Loader2 size={14} className="animate-spin" /> : <FileJson size={14} />} Download JSON
            </button>
            <button
              onClick={() => doExport('zip')}
              disabled={exportBusy === 'zip'}
              className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.16em] bg-gradient-to-r from-cyan-500 to-blue-500 text-white disabled:opacity-40 flex items-center gap-2"
              data-testid="export-zip-btn"
            >
              {exportBusy === 'zip' ? <Loader2 size={14} className="animate-spin" /> : <FileArchive size={14} />} Download ZIP
            </button>
          </div>
        </GlassCard>

        {/* Schedule deletion */}
        {loading ? null : scheduled ? (
          <GlassCard className="p-6 border-red-400/30">
            <div className="flex items-start gap-4 mb-3">
              <GradientIconTile icon={CalendarX} ramp="fuchsia" />
              <div>
                <h2 className="text-white font-bold font-syne">Deletion in progress</h2>
                <p className="text-xs text-red-300/85 mt-0.5">
                  Initiated by <strong>{scheduled.initiated_by === 'master' ? 'EntrepreNatives Support' : 'you'}</strong> · scheduled for {new Date(scheduled.scheduled_for).toLocaleString()}
                </p>
                {scheduled.reason && <p className="text-xs text-white/70 mt-2 italic">"{scheduled.reason}"</p>}
              </div>
            </div>
            {scheduled.initiated_by === 'self' ? (
              <button
                onClick={cancelDeletion}
                className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.16em] bg-emerald-500 hover:bg-emerald-400 text-emerald-950 flex items-center gap-2"
                data-testid="self-cancel-deletion"
              >
                <CheckCircle2 size={14} /> Cancel deletion
              </button>
            ) : (
              <a
                href="mailto:support@entreprenatives.com?subject=Appeal%3A%20site%20deletion"
                className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.16em] bg-white/10 hover:bg-white/15 text-white border border-white/15 inline-flex items-center gap-2"
                data-testid="appeal-master-deletion"
              >
                <AlertTriangle size={14} /> Appeal to support
              </a>
            )}
          </GlassCard>
        ) : (
          <GlassCard className="p-6 border-red-500/15">
            <div className="flex items-start gap-4 mb-5">
              <GradientIconTile icon={ShieldAlert} ramp="fuchsia" />
              <div>
                <h2 className="text-white font-bold font-syne">Schedule site deletion</h2>
                <p className="text-xs text-white/55 mt-0.5">
                  Free, anytime. Useful if you're moving to a different host. You can cancel during the grace window.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/55">Grace period (1–30 days)</label>
                <div className="flex items-center gap-3 mt-1.5">
                  <input
                    type="range" min="1" max="30" step="1"
                    value={delDays}
                    onChange={(e) => setDelDays(Number(e.target.value))}
                    className="flex-1 accent-red-400"
                    data-testid="self-deletion-days-slider"
                  />
                  <div className="w-16 text-center text-2xl font-bold text-red-300 font-syne tabular-nums">{delDays}</div>
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/55">Why are you leaving? (optional)</label>
                <textarea
                  rows={3}
                  value={delReason}
                  onChange={(e) => setDelReason(e.target.value)}
                  placeholder="Helps us improve. Not shared publicly."
                  className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/40 resize-none focus:outline-none focus:border-red-400/40"
                  data-testid="self-deletion-reason"
                />
              </div>
              <button
                onClick={submitDeletion}
                disabled={delBusy}
                className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.16em] bg-gradient-to-r from-red-500 to-rose-600 text-white disabled:opacity-40 flex items-center gap-2"
                data-testid="self-deletion-submit"
              >
                {delBusy ? <Loader2 size={14} className="animate-spin" /> : <CalendarX size={14} />} Schedule deletion
              </button>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
}
